/**
 * Image processing utilities for the BPMN editor.
 *
 * Provides resize, compression, and deduplication helpers to keep
 * image data embedded in the BPMN XML as small as possible.
 */

/** Default constraints for image optimisation. */
const DEFAULTS = {
  maxWidth: 1920,
  maxHeight: 1080,
  quality: 0.82,           // JPEG quality (0–1)
  maxInputBytes: 10 * 1024 * 1024, // 10 MB raw file limit
};

// ─── Image Optimisation ────────────────────────────────────────────

/**
 * Load a data-URL into an HTMLImageElement.
 * @param {string} dataUrl
 * @returns {Promise<HTMLImageElement>}
 */
function loadImage(dataUrl) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Image load failed'));
    img.src = dataUrl;
  });
}

/**
 * Draw an image onto an opaque white canvas of the given size.
 *
 * JPEG carries no alpha channel: without a ground laid down first, every
 * transparent pixel turns black. White is what these images sit on anywhere
 * they are shown — Eigenschaften-Panel, PDF-Seite, Lightbox-Karte.
 *
 * @param {HTMLImageElement} img
 * @param {number} width
 * @param {number} height
 * @returns {HTMLCanvasElement}
 */
function drawOnWhite(img, width, height) {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(img, 0, 0, width, height);
  return canvas;
}

/**
 * Resize and compress a base64 data-URL image.
 *
 * - Scales down proportionally so that neither dimension exceeds maxWidth / maxHeight.
 * - Always outputs JPEG; transparency is flattened onto white (see drawOnWhite).
 *
 * @param {string} dataUrl   Base64 data-URL (data:image/…;base64,…)
 * @param {object} [opts]
 * @param {number} [opts.maxWidth=1920]
 * @param {number} [opts.maxHeight=1080]
 * @param {number} [opts.quality=0.82]  JPEG quality 0–1
 * @returns {Promise<string>} Optimised base64 data-URL
 */
export async function optimizeImage(dataUrl, opts = {}) {
  const maxW = opts.maxWidth ?? DEFAULTS.maxWidth;
  const maxH = opts.maxHeight ?? DEFAULTS.maxHeight;
  const quality = opts.quality ?? DEFAULTS.quality;

  const img = await loadImage(dataUrl);
  const { naturalWidth: srcW, naturalHeight: srcH } = img;

  // Calculate target dimensions (proportional down-scale only)
  let targetW = srcW;
  let targetH = srcH;
  if (srcW > maxW || srcH > maxH) {
    const ratio = Math.min(maxW / srcW, maxH / srcH);
    targetW = Math.round(srcW * ratio);
    targetH = Math.round(srcH * ratio);
  }

  return drawOnWhite(img, targetW, targetH).toDataURL('image/jpeg', quality);
}

/**
 * Re-encode an image as JPEG at a fixed quality, keeping its pixel size.
 *
 * The PDF export needs this: jsPDF embeds the bytes of a data-URL verbatim,
 * so the compression has to happen before the image reaches the document.
 *
 * @param {string} dataUrl   data-URL of the source image
 * @param {number} [quality=0.7]  JPEG quality 0–1
 * @returns {Promise<string>} data:image/jpeg;base64,…
 */
export async function toJpegDataUrl(dataUrl, quality = 0.7) {
  const img = await loadImage(dataUrl);
  const width = img.naturalWidth || img.width;
  const height = img.naturalHeight || img.height;
  return drawOnWhite(img, width, height).toDataURL('image/jpeg', quality);
}

/**
 * Validate input file size before processing.
 * @param {File} file
 * @returns {{ ok: boolean, message?: string }}
 */
export function validateFileSize(file) {
  if (file.size > DEFAULTS.maxInputBytes) {
    const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
    return {
      ok: false,
      message: `Die Datei ist zu groß (${sizeMB} MB). Maximal erlaubt sind ${DEFAULTS.maxInputBytes / (1024 * 1024)} MB.`,
    };
  }
  return { ok: true };
}

// ─── Image Hashing (SHA-256) ───────────────────────────────────────

/**
 * Compute a hex SHA-256 hash for a data-URL string.
 * Uses the browser's SubtleCrypto API (requires HTTPS).
 * @param {string} dataUrl
 * @returns {Promise<string>} Hex digest (64 chars)
 */
export async function hashImageDataUrl(dataUrl) {
  const encoder = new TextEncoder();
  const data = encoder.encode(dataUrl);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

// ─── Version Snapshot Image Helpers ────────────────────────────────

/**
 * Regex to match `custom:imageUrl="..."` or `custom:imageUrl='...'` attributes.
 * Uses a non-greedy match on the attribute value.
 */
const IMAGE_ATTR_RE_DQ = /\s*custom:imageUrl="([^"]*)"/g;
const IMAGE_ATTR_RE_SQ = /\s*custom:imageUrl='([^']*)'/g;

/**
 * Regex to extract the element id closest before an imageUrl attribute.
 * Looks for id="…" on the same element tag.
 */
const ELEMENT_WITH_IMAGE_RE_DQ = /<[^>]*\bid="([^"]*)"[^>]*custom:imageUrl="([^"]*)"[^>]*\/?>/g;
const ELEMENT_WITH_IMAGE_RE_SQ = /<[^>]*\bid="([^"]*)"[^>]*custom:imageUrl='([^']*)'[^>]*\/?>/g;
// Also match when imageUrl comes before id
const ELEMENT_WITH_IMAGE_REV_DQ = /<[^>]*custom:imageUrl="([^"]*)"[^>]*\bid="([^"]*)"[^>]*\/?>/g;
const ELEMENT_WITH_IMAGE_REV_SQ = /<[^>]*custom:imageUrl='([^']*)'[^>]*\bid="([^"]*)"[^>]*\/?>/g;

/**
 * Extract all image data-URLs from a BPMN XML snapshot, mapping element IDs
 * to their respective data-URL. Returns { images: { elementId: dataUrl }, xml: strippedXml }.
 *
 * @param {string} xml  BPMN XML string
 * @returns {{ images: Record<string, string>, xml: string }}
 */
export function extractImagesFromXml(xml) {
  const images = {};

  // Collect id→dataUrl pairs (handle both attribute orderings)
  for (const re of [ELEMENT_WITH_IMAGE_RE_DQ, ELEMENT_WITH_IMAGE_RE_SQ]) {
    re.lastIndex = 0;
    let m;
    while ((m = re.exec(xml)) !== null) {
      const [, elementId, dataUrl] = m;
      if (dataUrl) images[elementId] = dataUrl;
    }
  }
  for (const re of [ELEMENT_WITH_IMAGE_REV_DQ, ELEMENT_WITH_IMAGE_REV_SQ]) {
    re.lastIndex = 0;
    let m;
    while ((m = re.exec(xml)) !== null) {
      const [, dataUrl, elementId] = m;
      if (dataUrl) images[elementId] = dataUrl;
    }
  }

  // Strip the imageUrl attributes from the XML
  let stripped = xml;
  stripped = stripped.replaceAll(IMAGE_ATTR_RE_DQ, '');
  stripped = stripped.replaceAll(IMAGE_ATTR_RE_SQ, '');

  return { images, xml: stripped };
}

/**
 * Build image references for a version history entry.
 * Maps each elementId to the SHA-256 hash of its image data-URL.
 * Also returns any new images that are not yet in the image store.
 *
 * @param {Record<string, string>} images         elementId → dataUrl
 * @param {Record<string, string>} existingStore   hash → dataUrl
 * @returns {Promise<{ refs: Record<string, string>, newImages: Record<string, string> }>}
 *          refs: elementId → hash,  newImages: hash → dataUrl
 */
export async function buildImageRefs(images, existingStore = {}) {
  const refs = {};
  const newImages = {};

  // Build reverse lookup: dataUrl → hash (for existing store entries)
  const existingByUrl = {};
  for (const [hash, url] of Object.entries(existingStore)) {
    existingByUrl[url] = hash;
  }

  for (const [elementId, dataUrl] of Object.entries(images)) {
    // Check if this exact dataUrl already exists in the store
    let hash = existingByUrl[dataUrl];
    if (!hash) {
      hash = await hashImageDataUrl(dataUrl);
      if (!existingStore[hash]) {
        newImages[hash] = dataUrl;
      }
    }
    refs[elementId] = hash;
  }

  return { refs, newImages };
}

/**
 * Re-inject image data-URLs into a stripped XML snapshot by looking up
 * hash references in the image store.
 *
 * For each elementId in `imageRefs`, finds the element tag by its id
 * attribute and inserts `custom:imageUrl="<dataUrl>"` before the closing
 * `>` or `/>`.
 *
 * @param {string} xml            Stripped BPMN XML (no imageUrl attributes)
 * @param {Record<string, string>} imageRefs   elementId → hash
 * @param {Record<string, string>} imageStore  hash → dataUrl
 * @returns {string} XML with imageUrl attributes re-injected
 */
export function rehydrateSnapshotImages(xml, imageRefs, imageStore) {
  if (!imageRefs || !imageStore) return xml;

  let result = xml;
  for (const [elementId, hash] of Object.entries(imageRefs)) {
    const dataUrl = imageStore[hash];
    if (!dataUrl) continue;

    // Find the element by id and inject the imageUrl attribute
    // Match: <... id="elementId" ...> or <... id="elementId" ... />
    const escapedId = elementId.replaceAll(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`);
    const re = new RegExp(
      String.raw`(<[^>]*\bid="${escapedId}"[^>]*?)(\/?>)`,
      'g'
    );
    result = result.replace(re, `$1 custom:imageUrl="${dataUrl}"$2`);
  }
  return result;
}

/**
 * Garbage-collect the image store: remove any images that are not referenced
 * by the current diagram or any version history entry.
 *
 * @param {Record<string, string>} imageStore        hash → dataUrl
 * @param {string[]} currentImageHashes              hashes used in the live diagram
 * @param {Array<{ images?: Record<string, string> }>} historyEntries  version history
 * @returns {Record<string, string>} cleaned image store
 */
export function garbageCollectImageStore(imageStore, currentImageHashes, historyEntries) {
  const referencedHashes = new Set(currentImageHashes);

  // Collect hashes referenced by any history entry
  for (const entry of historyEntries) {
    if (entry.images) {
      for (const hash of Object.values(entry.images)) {
        referencedHashes.add(hash);
      }
    }
  }

  // Keep only referenced images
  const cleaned = {};
  for (const [hash, dataUrl] of Object.entries(imageStore)) {
    if (referencedHashes.has(hash)) {
      cleaned[hash] = dataUrl;
    }
  }
  return cleaned;
}
